import React from 'react';
import { noop } from 'lodash';
import { I18n } from '@fiverr-private/i18n-react';
import { getContext } from '@fiverr-private/fiverr_context';
import { Container } from '@fiverr-private/layout_components';
import { Typography } from '@fiverr-private/typography';
import { redirectToSignUpPage, showMigrationModal, onMigrationSuccessDefault } from './utils';
import { TryHourlyRatesProps } from './types';

const T_PREFIX = 'business_entry_point.banners.hourly_rates';

interface TryHourlyRatesBannerProps
    extends TryHourlyRatesProps,
        Omit<React.ComponentProps<typeof Container>, 'onClick'> {
    titleSize?: React.ComponentProps<typeof Typography>['size'];
    titleFontWeight?: React.ComponentProps<typeof Typography>['fontWeight'];
    titleColor?: React.ComponentProps<typeof Typography>['color'];
    descriptionSize?: React.ComponentProps<typeof Typography>['size'];
    descriptionColor?: React.ComponentProps<typeof Typography>['color'];
    onTryHourlyRatesRedirect?(): void;
}

export const TryHourlyRatesBanner: React.FC<TryHourlyRatesBannerProps> = ({
    className,
    source,
    registrationSource,
    migrationBiEnrichment,
    titleSize = 'b_sm',
    titleFontWeight = 'semibold',
    titleColor = 'grey_1100',
    descriptionSize = 'b_sm',
    descriptionColor = 'grey_900',
    onMigrationSuccess = onMigrationSuccessDefault,
    onClick = noop,
    onTryHourlyRatesRedirect,
    ...containerProps
}) => {
    const { userId } = getContext();

    const onCtaClick = () => {
        onClick();
        if (userId) {
            showMigrationModal({ source, migrationBiEnrichment, onMigrationSuccess });
        } else if (onTryHourlyRatesRedirect) {
            onTryHourlyRatesRedirect();
        } else {
            redirectToSignUpPage({ registrationSource });
        }
    };

    const hasTryHourlyRateRedirect = typeof onTryHourlyRatesRedirect === 'function';

    return (
        <Container
            className={className}
            display="flex"
            direction="column"
            borderRadius="lg"
            paddingX="4"
            paddingY="4"
            gap="0.5"
            color="grey_1200"
            position="relative"
            backgroundColor="grey_100"
            borderColor="grey_400"
            borderWidth="sm"
            {...containerProps}
        >
            <Typography size={titleSize} fontWeight={titleFontWeight} as="p" color={titleColor}>
                <I18n k={`${T_PREFIX}.title`} />
            </Typography>
            <div>
                <Typography size={descriptionSize} color={descriptionColor} as="span" marginRight="1">
                    <I18n k={`${T_PREFIX}.description`} />
                </Typography>
                <Typography
                    as="a"
                    size={descriptionSize}
                    target="_blank"
                    color="business_800"
                    fontWeight="semibold"
                    decoration="underline"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
                    onClick={onCtaClick}
                >
                    <span>
                        {userId ? (
                            <I18n k={`${T_PREFIX}.join_fiverr_pro`} />
                        ) : (
                            <I18n
                                k={hasTryHourlyRateRedirect ? `${T_PREFIX}.try_hourly_rates` : `${T_PREFIX}.sign_up`}
                            />
                        )}
                    </span>
                </Typography>
            </div>
        </Container>
    );
};
